import { useEffect, useState } from 'react'
import { ChevronDown, ChevronRight, GitBranch, AlertTriangle, ArrowUpRight, ArrowDownRight, ExternalLink, Crosshair, Brain } from 'lucide-react'
import { useGetTaskActionQuery, useResolveTaskActionPointsMutation, type TaskActionNeighbour } from '../services/taskActionsApi'
import { taskDetailUrl } from '../lib/taskDetailPath'
import { shortActionId } from '../lib/taskActionId'

// The light per-action row served in the task-detail envelope (see lib/task-actions.ts
// listTaskActionMetaByTaskIds). Payloads are NOT here — they load per action when a row is expanded.
export interface TaskActionMeta {
  id: string
  task_id: string
  seq: number
  kind: string
  label: string | null
  status: string
  error: string | null
  created_at: string
  started_at: string | null
  ended_at: string | null
  data_bytes: number | null
  llm_call_count: number
  // The ids of the actions this one consumed, in the order they were produced. Rendered on the row itself,
  // so the graph is readable by scanning the list instead of by expanding rows one at a time.
  depends_on_ids: string[]
  depends_on_count: number
  consumed_by_count: number
  // Where this action happened in the driver recording, resolved from the console marker it emitted while
  // running. Null until "Locate in recording" has been run (or forever, with no driver recording).
  recording_id: string | null
  recording_point: string | null
  recording_time_ms: number | null
}

function formatTime(iso: string) {
  return new Date(iso).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit' })
}
function formatElapsed(start: string | null, end: string | null): string {
  if (!start || !end) return ''
  const ms = new Date(end).getTime() - new Date(start).getTime()
  if (!Number.isFinite(ms) || ms < 0) return ''
  return ms >= 1000 ? `${(ms / 1000).toFixed(1)}s` : `${ms}ms`
}
function formatBytes(n: number | null): string {
  if (n == null || n === 0) return ''
  if (n >= 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)}MB`
  if (n >= 1024) return `${Math.round(n / 1024)}KB`
  return `${n}B`
}

// Replay opens a recording at a POINT when given one (the execution position the marker was logged at),
// falling back to a time offset. Either lands the reader on the action instead of at the recording's start.
function recordingUrl(a: { recording_id: string | null; recording_point: string | null; recording_time_ms: number | null }): string | null {
  if (!a.recording_id) return null
  const base = `https://app.replay.io/recording/${a.recording_id}`
  if (a.recording_point) return `${base}?point=${a.recording_point}${a.recording_time_ms != null ? `&time=${a.recording_time_ms}` : ''}`
  if (a.recording_time_ms != null) return `${base}?time=${a.recording_time_ms}`
  return base
}
function formatOffset(ms: number | null): string {
  if (ms == null) return ''
  const s = ms / 1000
  return s >= 60 ? `${Math.floor(s / 60)}m${String(Math.floor(s % 60)).padStart(2, '0')}s` : `${s.toFixed(1)}s`
}


// How many dependency ids a row shows before it stops; the rest are counted. A row's inputs are normally
// one to three, and the outliers (a summary consuming every step) would otherwise wrap the row into a
// paragraph — the full list is in the expanded panel, one click away.
const INLINE_DEPS_SHOWN = 6

const idStyle: React.CSSProperties = { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontVariantNumeric: 'tabular-nums' }

function Pre({ children }: { children: React.ReactNode }) {
  return (
    <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 320, overflow: 'auto', margin: 0, padding: 8, background: 'rgba(127,127,127,0.08)', borderRadius: 4, fontSize: 11.5 }}>
      {children}
    </pre>
  )
}
function Block({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ opacity: 0.55, textTransform: 'uppercase', fontSize: 10, letterSpacing: 0.5, marginBottom: 2 }}>{label}</div>
      {children}
    </div>
  )
}

// One dependency edge. An edge to an action in ANOTHER task links to that task's page and deep-links the
// row (#action-<id>); a same-task edge expands in place. An edge whose row is missing — pruned, or a write
// that never landed — is shown as a dangling id rather than dropped, because "the link is broken" and
// "there was no link" are different diagnoses.
function NeighbourLink({
  neighbour, projectId, currentTaskId, relativeToId, onSelect,
}: {
  neighbour: TaskActionNeighbour
  projectId: string
  currentTaskId: string
  // The action this neighbour is listed under, so its id can be shortened to `#<seq>` when the two share a
  // task — and stays full, which is the visible signal, when the edge crosses one.
  relativeToId: string
  onSelect: (actionId: string) => void
}) {
  const shortId = shortActionId(neighbour.id, relativeToId)
  const text = `${neighbour.kind ?? '(unknown action)'}${neighbour.label ? ` — ${neighbour.label}` : ''}`
  const isError = neighbour.status === 'error'
  const style: React.CSSProperties = { color: isError ? '#e5534b' : 'inherit', textAlign: 'left', background: 'none', border: 'none', padding: 0, cursor: 'pointer', textDecoration: 'underline', fontSize: 11.5 }

  if (!neighbour.task_id) {
    return <div style={{ opacity: 0.5, fontSize: 11.5 }} title={neighbour.id}><span style={idStyle}>{shortId}</span> (no longer recorded)</div>
  }
  if (neighbour.task_id !== currentTaskId) {
    return (
      <a
        href={`${taskDetailUrl(projectId, neighbour.task_id)}#action-${neighbour.id}`}
        style={{ ...style, display: 'inline-flex', alignItems: 'center', gap: 4 }}
        title={neighbour.id}
        data-analytics-event="task-action-cross-task-open"
      >
        <span style={idStyle}>{shortId}</span> {text} <ExternalLink size={10} /> <span style={{ opacity: 0.6, textDecoration: 'none' }}>other task</span>
      </a>
    )
  }
  return (
    <button type="button" style={style} title={neighbour.id} onClick={() => onSelect(neighbour.id)} data-analytics-event="task-action-dep-open">
      <span style={idStyle}>{shortId}</span> {text}
    </button>
  )
}

// One expandable action row: its metadata line and — once expanded — the payload, the LLM calls it made,
// and both directions of the graph around it, fetched from /api/task-action/:id.
function ActionRow({
  action, projectId, idScopeId, expanded, onToggle, onSelect,
}: {
  action: TaskActionMeta
  projectId: string
  // An action id every row of this list can be shortened against, or null when the list spans more than one
  // task (then `#<seq>` would name two different actions and every row shows its full id instead).
  idScopeId: string | null
  expanded: boolean
  onToggle: () => void
  onSelect: (actionId: string) => void
}) {
  const { data: detail, isFetching, isError } = useGetTaskActionQuery(action.id, { skip: !expanded, pollingInterval: expanded ? 5000 : 0, refetchOnMountOrArgChange: true })
  const failed = action.status === 'error'

  return (
    <div id={`action-${action.id}`} style={{ borderTop: '1px solid var(--border-color, #2a2a2a)' }}>
      <button
        type="button"
        onClick={onToggle}
        data-testid="task-action-row"
        data-analytics-event="task-action-toggle"
        style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left', background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', padding: '6px 4px', fontSize: 13, flexWrap: 'wrap' }}
      >
        {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        <span style={{ opacity: 0.7, fontVariantNumeric: 'tabular-nums' }}>{formatTime(action.created_at)}</span>
        {/* The row's OWN id — what a dependency elsewhere on the page (or in another task, or in an eval
            investigation finding) names it by. Full id on hover, since that is the form other tools take. */}
        <span style={{ ...idStyle, opacity: 0.75 }} title={action.id} data-testid="task-action-id">
          {shortActionId(action.id, idScopeId)}
        </span>
        <span style={{ fontWeight: 600 }}>{action.kind}</span>
        {action.label && <span style={{ opacity: 0.8 }}>{action.label}</span>}
        {action.status === 'running' && <span style={{ opacity: 0.7 }}>running</span>}
        {failed && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, color: '#e5534b' }}>
            <AlertTriangle size={11} /> error
          </span>
        )}
        {formatElapsed(action.started_at, action.ended_at) && (
          <span style={{ opacity: 0.6 }}>{formatElapsed(action.started_at, action.ended_at)}</span>
        )}
        {action.llm_call_count > 0 && <span style={{ opacity: 0.75 }}>{action.llm_call_count} LLM</span>}
        {recordingUrl(action) && (
          // Stops the row's expand/collapse: this is a link out, not a disclosure.
          <a
            href={recordingUrl(action)!}
            target="_blank"
            rel="noreferrer"
            onClick={e => e.stopPropagation()}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 3, color: 'inherit', opacity: 0.85 }}
            data-testid="task-action-recording-link"
            data-analytics-event="task-action-recording-open"
            title="Open the driver recording where this action ran"
          >
            <Crosshair size={11} /> {formatOffset(action.recording_time_ms) || 'in recording'}
          </a>
        )}
        <span style={{ marginLeft: 'auto', opacity: 0.5, fontSize: 11, display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          {/* WHICH actions this one consumed, not just how many: `← #14 #15` is the edge itself, so a run's
              chain can be followed down the list without expanding a row. A dependency in ANOTHER task keeps
              its full id — that is the one edge worth spelling out, since it leaves this page. */}
          {action.depends_on_ids.length > 0 && (
            <span style={idStyle} title={`Consumed: ${action.depends_on_ids.join(', ')}`} data-testid="task-action-deps">
              ← {action.depends_on_ids.slice(0, INLINE_DEPS_SHOWN).map(id => shortActionId(id, idScopeId)).join(' ')}
              {action.depends_on_ids.length > INLINE_DEPS_SHOWN && ` +${action.depends_on_ids.length - INLINE_DEPS_SHOWN}`}
            </span>
          )}
          {action.consumed_by_count > 0 && <span title="Actions that consumed this one">{`→ ${action.consumed_by_count}`}</span>}
          {formatBytes(action.data_bytes)}
        </span>
      </button>

      {expanded && (
        <div style={{ padding: '4px 4px 12px 26px', fontSize: 12 }}>
          {/* The FULL id (and the task it belongs to), from the row rather than the fetch so it is there
              while the payload loads. `user-select: all` because this id's other life is being pasted into a
              query_table filter or an investigation finding, where the shortened form means nothing. */}
          <Block label="Id">
            <span style={{ ...idStyle, fontSize: 11.5, userSelect: 'all' }}>{action.id}</span>
            <span style={{ opacity: 0.55, marginLeft: 8, fontSize: 11.5 }}>
              seq {action.seq} · task <span style={{ ...idStyle, userSelect: 'all' }}>{action.task_id}</span>
            </span>
          </Block>
          {isFetching && <p style={{ opacity: 0.6 }}>Loading…</p>}
          {isError && <p style={{ color: '#e5534b' }}>Failed to load action detail.</p>}
          {action.error && <Block label="Error"><span style={{ color: '#e5534b' }}>{action.error}</span></Block>}
          {detail && (
            <>
              {Object.keys(detail.timing_events).length > 0 && (
                <Block label="Timing">
                  <table style={{ width: '100%', textAlign: 'left' }}>
                    <thead><tr><th>Time</th><th>Elapsed</th><th>Event</th></tr></thead>
                    <tbody>{Object.values(detail.timing_events).sort((a, b) => a.event_seq - b.event_seq).map(event => (
                      <tr key={event.event_seq}>
                        <td>{event.at}</td>
                        <td>{detail.started_at ? `${((Date.parse(event.at) - Date.parse(detail.started_at)) / 1000).toFixed(3)}s` : '—'}</td>
                        <td>{event.name}{event.details && <Pre>{JSON.stringify(event.details, null, 2)}</Pre>}</td>
                      </tr>
                    ))}</tbody>
                  </table>
                </Block>
              )}
              {detail.meta && Object.keys(detail.meta).length > 0 && (
                <Block label="Meta"><Pre>{JSON.stringify(detail.meta, null, 2)}</Pre></Block>
              )}
              <Block label="Data"><Pre>{detail.data == null ? '(none)' : JSON.stringify(detail.data, null, 2)}</Pre></Block>
              {detail.llm_job_ids && detail.llm_job_ids.length > 0 && (
                <Block label={`LLM calls (${detail.llm_job_ids.length})`}>
                  {/* The prompts are not copied onto the action — they are the llm_jobs rows the LLM Calls
                      section on this page already lists. Each id links to its row there, which opens and
                      loads the full messages + completion, so the prompt behind a decision is one click
                      from the decision. */}
                  {detail.llm_job_ids.map(jobId => (
                    <div key={jobId}>
                      <a
                        href={`#llm-call-${jobId}`}
                        style={{ fontSize: 11.5, display: 'inline-flex', alignItems: 'center', gap: 4 }}
                        data-testid="task-action-llm-link"
                        data-analytics-event="task-action-llm-call-open"
                      >
                        <Brain size={11} /> view prompt + completion
                        <span style={{ opacity: 0.55 }}>{jobId}</span>
                      </a>
                    </div>
                  ))}
                </Block>
              )}
              {detail.depends_on.length > 0 && (
                <Block label={`Depends on (${detail.depends_on.length})`}>
                  {detail.depends_on.map(n => (
                    <div key={n.id} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <ArrowUpRight size={11} style={{ opacity: 0.5, flexShrink: 0 }} />
                      <NeighbourLink neighbour={n} projectId={projectId} currentTaskId={detail.task_id} relativeToId={detail.id} onSelect={onSelect} />
                    </div>
                  ))}
                </Block>
              )}
              {detail.consumed_by.length > 0 && (
                <Block label={`Consumed by (${detail.consumed_by.length})`}>
                  {detail.consumed_by.map(n => (
                    <div key={n.id} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <ArrowDownRight size={11} style={{ opacity: 0.5, flexShrink: 0 }} />
                      <NeighbourLink neighbour={n} projectId={projectId} currentTaskId={detail.task_id} relativeToId={detail.id} onSelect={onSelect} />
                    </div>
                  ))}
                </Block>
              )}
            </>
          )}
        </div>
      )}
    </div>
  )
}

/**
 * Collapsible list of every ACTION a task performed — each computation or LLM decision it made, what it
 * produced, and which other actions' output it consumed (see netlify/functions/lib/task-actions.ts).
 *
 * This is the chain from "the page looked like this" through "so the handler decided to file" to "so this
 * report was submitted and judged", readable in one place instead of across task_logs, llm_jobs and the
 * recording. Dependencies link both ways, and a dependency on ANOTHER task's action (a followup's filing
 * depending on the journey run's decision) links to that task's page and deep-links the row.
 *
 * Every row carries its ID and the ids it CONSUMED (`#12 journey.step … ← #9 #11`), so the graph reads off
 * the list and an action can be named in a report or a `query_table` filter without expanding anything.
 * While the list is one task's actions those ids are the seq alone; an id that crosses a task boundary is
 * shown in full, which is what distinguishes the two at a glance.
 *
 * Admin-only, rendered inside the admin task page.
 */
export default function TaskActionsSection({
  actions, projectId, classPrefix,
}: {
  actions: TaskActionMeta[]
  projectId: string
  classPrefix: string
}) {
  const [expanded, setExpanded] = useState(false)
  const [openIds, setOpenIds] = useState<Set<string>>(new Set())
  // Which KINDS are open. Collapsed by default so the section opens on a readable index of what the task
  // did — a journey run records hundreds of actions across a dozen kinds, and dumping every row makes the
  // shape of the run harder to see, not easier.
  const [openKinds, setOpenKinds] = useState<Set<string>>(new Set())
  const [resolvePoints, { isLoading: resolving }] = useResolveTaskActionPointsMutation()
  // What the last resolve attempt said. Shown verbatim: "0 resolved" has several distinct causes (no driver
  // recording, markers not indexed yet, the read failed) and they need different responses from the reader.
  const [resolveNote, setResolveNote] = useState<string | null>(null)

  // Deep link (#action-<id>): open the list, the row's KIND group, and the row itself, then scroll to it —
  // the landing spot when a cross-task dependency link is followed from another task's page. Re-read on
  // hashchange because the link may be same-page (a dependency inside this task).
  useEffect(() => {
    const read = () => {
      const hash = window.location.hash
      if (!hash.startsWith('#action-')) return
      const id = hash.slice('#action-'.length)
      const target = actions.find(a => a.id === id)
      if (!target) return
      setExpanded(true)
      setOpenKinds(prev => new Set(prev).add(target.kind))
      setOpenIds(prev => new Set(prev).add(id))
      // The row only exists once its group is open, so wait a frame before scrolling to it.
      window.setTimeout(() => document.getElementById(`action-${id}`)?.scrollIntoView({ block: 'center' }), 50)
    }
    read()
    window.addEventListener('hashchange', read)
    return () => window.removeEventListener('hashchange', read)
  }, [actions])

  // A task that recorded NOTHING says so, rather than rendering nothing. An absent section is
  // indistinguishable from a feature that isn't deployed — and the tasks with no actions are exactly the
  // ones worth naming: a task kind that isn't instrumented yet, or a run that died before its first action.
  if (!actions.length) {
    return (
      <section className={`${classPrefix}__section`}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, opacity: 0.6, padding: '6px 0' }} data-testid="task-actions-empty">
          <GitBranch size={15} /> Actions (0)
          <span style={{ fontSize: 12 }}>— nothing recorded for this task (its kind may not be instrumented yet)</span>
        </div>
      </section>
    )
  }

  const toggleRow = (id: string) => setOpenIds(prev => {
    const next = new Set(prev)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    return next
  })
  const toggleKind = (kind: string) => setOpenKinds(prev => {
    const next = new Set(prev)
    if (next.has(kind)) next.delete(kind)
    else next.add(kind)
    return next
  })
  // Following a dependency to another action IN THIS TASK: open its kind group as well, or the row it points
  // at would be inside a collapsed group and the click would appear to do nothing.
  const selectRow = (id: string) => {
    const target = actions.find(a => a.id === id)
    if (target) setOpenKinds(prev => new Set(prev).add(target.kind))
    setOpenIds(prev => new Set(prev).add(id))
    window.setTimeout(() => document.getElementById(`action-${id}`)?.scrollIntoView({ block: 'center' }), 50)
  }

  // ONE group per complete kind, holding every action of that kind. Groups are ordered by where the kind
  // first appears, so the list still reads in execution order (args → turns → mcp → submit → judges →
  // filed) while each kind is one line the reader can open.
  //
  // This replaced grouping by a truncated kind prefix over CONSECUTIVE runs, which produced neighbouring
  // `filebug` and `filebug.judge` headings — two granularities of the same name, and the same kind split
  // across several groups whenever another kind interleaved. There is no ambiguity to model here: an
  // action's kind is its identity, so it is the grouping.
  const byKind = new Map<string, TaskActionMeta[]>()
  for (const a of actions) {
    const existing = byKind.get(a.kind)
    if (existing) existing.push(a)
    else byKind.set(a.kind, [a])
  }
  const groups = [...byKind.entries()].map(([key, kindActions]) => ({ key, actions: kindActions }))
  const failedCount = actions.filter(a => a.status === 'error').length
  const located = actions.filter(a => a.recording_id).length
  // Ids are shortened to `#<seq>` only while the list is ONE task's actions — a test run with two journey
  // tasks lists both, and there `#7` would name an action twice, so both rows show their full id.
  const idScopeId = new Set(actions.map(a => a.task_id)).size === 1 ? actions[0]!.id : null

  async function locateInRecording() {
    setResolveNote(null)
    try {
      const res = await resolvePoints(actions[0]!.task_id).unwrap()
      setResolveNote(res.resolved > 0
        ? `Located ${res.resolved} of ${actions.length} actions — reload to see the links.`
        : res.reason ?? 'Nothing could be located.')
    } catch {
      setResolveNote('Locating failed.')
    }
  }

  return (
    <section className={`${classPrefix}__section`}>
      <button type="button" className={`${classPrefix}__section-toggle`} onClick={() => setExpanded(!expanded)} data-analytics-event="task-actions-toggle" data-testid="task-actions-toggle">
        {expanded ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
        <GitBranch size={15} /> Actions ({actions.length})
        {failedCount > 0 && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, color: '#e5534b', marginLeft: 6 }} data-testid="task-actions-failed-count">
            <AlertTriangle size={12} /> {failedCount} failed
          </span>
        )}
        {located > 0 && <span style={{ opacity: 0.6, fontSize: 12, marginLeft: 6 }}>{located} located in recording</span>}
      </button>
      {expanded && located === 0 && (
        // Each action logged a marker into the driver recording as it ran; this reads them back and stamps
        // every row's point in one pass. Offered rather than automatic because it costs a Replay MCP read.
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 4px 8px', fontSize: 12 }}>
          <button
            type="button"
            onClick={locateInRecording}
            disabled={resolving}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: 'none', border: '1px solid var(--border-color, #2a2a2a)', borderRadius: 4, padding: '3px 8px', color: 'inherit', cursor: resolving ? 'default' : 'pointer' }}
            data-testid="task-actions-locate"
            data-analytics-event="task-actions-locate-in-recording"
          >
            <Crosshair size={12} /> {resolving ? 'Locating…' : 'Locate in recording'}
          </button>
          {resolveNote && <span style={{ opacity: 0.7 }}>{resolveNote}</span>}
        </div>
      )}
      {expanded && (
        <div className={`${classPrefix}__logs`}>
          {groups.map(g => {
            const groupOpen = openKinds.has(g.key)
            const groupFailed = g.actions.filter(a => a.status === 'error').length
            return (
              <div key={g.key} style={{ marginBottom: 6 }} data-testid="task-action-group">
                <button
                  type="button"
                  onClick={() => toggleKind(g.key)}
                  style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left', padding: '6px 4px', fontSize: 12, fontWeight: 600, opacity: 0.9, background: 'none', border: 'none', borderBottom: '1px solid var(--border-color, #2a2a2a)', color: 'inherit', cursor: 'pointer' }}
                  data-testid="task-action-group-toggle"
                  data-analytics-event="task-action-group-toggle"
                >
                  {groupOpen ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                  <span>{g.key}</span>
                  <span style={{ opacity: 0.6, fontWeight: 400 }}>
                    {g.actions.length} {g.actions.length === 1 ? 'action' : 'actions'}
                  </span>
                  {groupFailed > 0 && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, color: '#e5534b', fontWeight: 400 }}>
                      <AlertTriangle size={11} /> {groupFailed}
                    </span>
                  )}
                </button>
                {groupOpen && g.actions.map(a => (
                  <ActionRow
                    key={a.id}
                    action={a}
                    projectId={projectId}
                    idScopeId={idScopeId}
                    expanded={openIds.has(a.id)}
                    onToggle={() => toggleRow(a.id)}
                    onSelect={selectRow}
                  />
                ))}
              </div>
            )
          })}
        </div>
      )}
    </section>
  )
}
